import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { IconAlertTriangle, IconCircleCheck, IconDeviceDesktop } from "@tabler/icons-react";
import moment from "moment";
import { axiosInstance } from "../../utils/axiosInstance";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  EmptyState,
  ErrorBanner,
  PageHeader,
  Pill,
  RefreshButton,
  SkeletonRows,
  SkeletonStats,
  Stat,
  Tone,
} from "../ui";
import {
  OpsKioskOnlineStatus,
  OpsKioskRow,
  OpsKiosksPayload,
} from "./kioskFleetTypes";

const POLL_MS = 60_000;

function formatGeneratedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = moment(iso);
  return m.isValid() ? m.fromNow() : "—";
}

function formatAbsolute(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = moment(iso);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm:ss") : iso;
}

function formatMoneyMinor(amountMinor: number, currency: string): string {
  const major = (Number(amountMinor) || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "GBP",
    }).format(major);
  } catch {
    return `£${major.toFixed(2)}`;
  }
}

function onlineLabel(status: OpsKioskOnlineStatus): string {
  if (status === "online") return "Online";
  if (status === "offline") return "Offline";
  return "Never seen";
}

function onlineTone(status: OpsKioskOnlineStatus): Tone {
  if (status === "online") return "good";
  if (status === "offline") return "bad";
  return "neutral";
}

function failCell(count: number) {
  return count > 0 ? (
    <span className="sq-value-bad">{count}</span>
  ) : (
    <span className="sq-muted">0</span>
  );
}

function storeCell(row: OpsKioskRow) {
  const name = row.locationName || "Unknown store";
  return (
    <>
      {row.layoutSiteId ? (
        <Link
          to={`/status?siteId=${encodeURIComponent(row.layoutSiteId)}`}
          className="sq-text-link"
          title={`Open status for ${name}`}
        >
          {name}
        </Link>
      ) : (
        <span className="sq-strong">{name}</span>
      )}
      {row.locationPin ? <div className="sq-muted">{row.locationPin}</div> : null}
    </>
  );
}

export default function KioskFleet() {
  const [data, setData] = useState<OpsKiosksPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const visibleRef = useRef(true);

  const load = useCallback(async (opts?: { soft?: boolean }) => {
    if (opts?.soft) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get<OpsKiosksPayload>("/ops/kiosks");
      setData(res.data);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load kiosk fleet.";
      setError(msg);
      if (!opts?.soft) setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onVisibility = () => {
      visibleRef.current = document.visibilityState === "visible";
    };
    document.addEventListener("visibilitychange", onVisibility);
    const id = window.setInterval(() => {
      if (visibleRef.current) load({ soft: true });
    }, POLL_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(id);
    };
  }, [load]);

  return (
    <PageContainer>
      <PageHeader
        title="Kiosks"
        subtitle={
          data?.generatedAt
            ? `Updated ${formatGeneratedAt(data.generatedAt)}${refreshing ? " · refreshing…" : ""}`
            : undefined
        }
        actions={
          <RefreshButton onClick={() => load({ soft: true })} loading={loading || refreshing} />
        }
      />

      {error ? <ErrorBanner onRetry={() => load()}>{error}</ErrorBanner> : null}

      {loading && !data ? (
        <div className="sq-stack" role="status" aria-label="Loading kiosk fleet">
          <SkeletonStats />
          <Card>
            <SkeletonRows rows={6} />
          </Card>
        </div>
      ) : null}

      {data ? (
        <div className="sq-stack">
          <section className="sq-stats" aria-label="Fleet totals">
            <Stat icon={<IconDeviceDesktop size={16} />} label="Kiosks" value={data.totals.kioskCount} />
            <Stat icon={<IconCircleCheck size={16} />} label="Online" value={data.totals.onlineCount} />
            <Stat
              icon={<IconAlertTriangle size={16} />}
              label="Payment fails (24h)"
              tone={data.totals.paymentFails24h > 0 ? "warn" : undefined}
              value={data.totals.paymentFails24h}
            />
            <Stat
              icon={<IconAlertTriangle size={16} />}
              label="Start fails (24h)"
              tone={data.totals.startFails24h > 0 ? "bad" : undefined}
              value={data.totals.startFails24h}
            />
          </section>

          <Card
            flush
            title="Fleet"
            subtitle="Online status, version, last sale, last call for help, and 24h failures."
            aria-label="Kiosk fleet"
          >
            {data.kiosks.length === 0 ? (
              <EmptyState icon={<IconDeviceDesktop size={24} />} title="No kiosks reported yet">
                Devices appear after a heartbeat or first sale.
              </EmptyState>
            ) : (
              <div className="sq-table-wrap">
                <table className="sq-table is-stacked">
                  <thead>
                    <tr>
                      <th scope="col">Store</th>
                      <th scope="col">Status</th>
                      <th scope="col">Version</th>
                      <th scope="col">Last sale</th>
                      <th scope="col">Last help</th>
                      <th scope="col" className="num">Pay fails</th>
                      <th scope="col" className="num">Start fails</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.kiosks.map((row) => (
                      <tr key={row.kioskId}>
                        <td className="cell-title">
                          {storeCell(row)}
                          <div className="sq-mono">{row.kioskId}</div>
                        </td>
                        <td data-label="Status">
                          <Pill tone={onlineTone(row.onlineStatus)}>
                            {onlineLabel(row.onlineStatus)}
                          </Pill>
                          {row.lastSeenAt ? (
                            <div className="sq-muted mt-1">Seen {formatAgo(row.lastSeenAt)}</div>
                          ) : null}
                        </td>
                        <td data-label="Version">{row.appVersion || "—"}</td>
                        <td data-label="Last sale" title={formatAbsolute(row.lastSale?.createdAt)}>
                          {row.lastSale ? (
                            <>
                              <div className="sq-strong">{formatAgo(row.lastSale.createdAt)}</div>
                              <div className="sq-muted">
                                {formatMoneyMinor(row.lastSale.amountMinor, row.lastSale.currency)} ·{" "}
                                {row.lastSale.status}
                              </div>
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td data-label="Last help" title={formatAbsolute(row.lastHelp?.createdAt)}>
                          {row.lastHelp ? (
                            <>
                              <div className="sq-strong">{formatAgo(row.lastHelp.createdAt)}</div>
                              {row.lastHelp.step ? (
                                <div className="sq-muted">Step: {row.lastHelp.step}</div>
                              ) : null}
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td data-label="Pay fails" className="num">
                          {failCell(row.paymentFails24h)}
                        </td>
                        <td data-label="Start fails" className="num">
                          {failCell(row.startFails24h)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      ) : null}
    </PageContainer>
  );
}
